import type {
    DropdownMenuCheckboxItemProps,
    DropdownMenuContentProps,
    DropdownMenuItemProps,
    DropdownMenuProps,
    DropdownMenuRadioGroupProps,
    DropdownMenuRadioItemProps,
    DropdownMenuSubContentProps,
    DropdownMenuSubProps,
    DropdownMenuSubTriggerProps,
    DropdownMenuTriggerProps,
} from '@radix-ui/react-dropdown-menu';
import {
    CheckboxItem,
    Content,
    Group,
    Item,
    ItemIndicator,
    Label,
    Portal,
    RadioGroup,
    RadioItem,
    Root,
    Separator,
    Sub,
    SubContent,
    SubTrigger,
    Trigger,
} from '@radix-ui/react-dropdown-menu';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';

import { Tooltip } from '../tooltip/Tooltip';

// biome-ignore lint/complexity/noBannedTypes: suppressed due to migration
export type DropdownMenuSeparatorProps = {};

export type DropdownMenuLabelProps = {
    readonly children: ReactNode;
};

export type DropdownMenuGroupProps = {
    readonly children: ReactNode;
};

const contentClasses = clsx(
    'bg-gray-100 border border-gray-300 rounded-xl shadow-md font-sans',
    'min-w-[220px] overflow-hidden p-xs z-[1000]',
);

const baseItemClasses = clsx(
    'flex items-center rounded-sm text-gray-900 cursor-pointer',
    'text-xxs font-normal leading-[1rem]',
    'py-s px-m relative select-none',
    'hover:bg-gray-200',
    'data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
);

const highlightedClasses =
    'data-[highlighted]:bg-gray-200 data-[highlighted]:text-gray-900 data-[highlighted]:outline-none';

const checkedClasses = 'data-[state=checked]:bg-gray-200';

export const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({ children, onOpenChange, open, ...props }) => {
    return (
        <Root open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Root>
    );
};

export const DropdownMenuTrigger: FunctionComponent<PropsWithChildren<DropdownMenuTriggerProps>> = ({
    children,
    ...props
}) => {
    return (
        <Trigger asChild={true} {...props}>
            {children}
        </Trigger>
    );
};

export const DropdownMenuItem: FunctionComponent<DropdownMenuItemProps & { readonly tooltipContent?: string }> = ({
    children,
    className,
    disabled,
    onSelect,
    tooltipContent,
    ...props
}) => {
    const menuItem = (
        <Item
            className={clsx(
                baseItemClasses,
                'gap-xs',
                '[&_svg]:text-gray-600',
                'data-[highlighted]:bg-gray-300 data-[highlighted]:text-gray-900 data-[highlighted]:outline-none',
                'data-[disabled]:[&_svg]:text-gray-400',
                className,
            )}
            disabled={disabled}
            onSelect={onSelect}
            {...props}
        >
            {children}
        </Item>
    );

    if (disabled === true && is.nonEmptyString(tooltipContent)) {
        return (
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <div style={{ cursor: 'not-allowed', display: 'flex' }}>{menuItem}</div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="right" sideOffset={5}>
                        {tooltipContent}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        );
    }

    return menuItem;
};

export const DropdownMenuCheckboxItem: FunctionComponent<DropdownMenuCheckboxItemProps> = ({
    checked,
    children,
    className,
    disabled,
    onCheckedChange,
    ...props
}) => {
    return (
        <CheckboxItem
            checked={checked}
            className={clsx(baseItemClasses, highlightedClasses, checkedClasses, className)}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
            {...props}
        >
            {children}
        </CheckboxItem>
    );
};

export const DropdownMenuSeparator: FunctionComponent<DropdownMenuSeparatorProps> = ({ ...props }) => {
    return <Separator className="bg-gray-300 h-px m-xs" {...props} />;
};

export const DropdownMenuLabel: FunctionComponent<DropdownMenuLabelProps> = ({ children, ...props }) => {
    return (
        <Label className="text-gray-700 text-xxs font-normal p-s" {...props}>
            {children}
        </Label>
    );
};

export const DropdownMenuGroup: FunctionComponent<DropdownMenuGroupProps> = ({ children, ...props }) => {
    return (
        <Group
            className="data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none"
            {...props}
        >
            {children}
        </Group>
    );
};

export const DropdownMenuSub: FunctionComponent<DropdownMenuSubProps> = ({
    children,
    onOpenChange,
    open,
    ...props
}) => {
    return (
        <Sub open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Sub>
    );
};

export const DropdownMenuRadioGroup: FunctionComponent<DropdownMenuRadioGroupProps> = ({ children, ...props }) => {
    return (
        <RadioGroup
            className="data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none"
            {...props}
        >
            {children}
        </RadioGroup>
    );
};

export const DropdownMenuRadioItem: FunctionComponent<DropdownMenuRadioItemProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <RadioItem className={clsx(baseItemClasses, highlightedClasses, checkedClasses, className)} {...props}>
            {children}
        </RadioItem>
    );
};

export const DropdownMenuSubTrigger: FunctionComponent<DropdownMenuSubTriggerProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <SubTrigger
            className={clsx(
                baseItemClasses,
                highlightedClasses,
                'justify-between',
                '[&_svg]:text-gray-600',
                'data-[state=open]:bg-gray-100',
                'data-[disabled]:[&_svg]:text-gray-400',
                className,
            )}
            {...props}
        >
            {children}
        </SubTrigger>
    );
};

export const DropdownMenuSubContent: FunctionComponent<DropdownMenuSubContentProps> = ({
    alignOffset,
    children,
    className,
    sideOffset,
    ...props
}) => {
    return (
        <Portal>
            <SubContent
                alignOffset={alignOffset}
                className={clsx(contentClasses, className)}
                sideOffset={sideOffset}
                {...props}
            >
                {children}
            </SubContent>
        </Portal>
    );
};

export const DropdownMenuContent: FunctionComponent<DropdownMenuContentProps> = ({ children, className, ...props }) => {
    return (
        <Portal>
            <Content className={clsx(contentClasses, className)} {...props}>
                {children}
            </Content>
        </Portal>
    );
};

export const DropdownMenuItemIndicator: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return (
        <ItemIndicator className="inline-flex items-center justify-center text-gray-900 ml-s">{children}</ItemIndicator>
    );
};

export const DropdownMenuSubTriggerContent: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <div className="flex items-center gap-xs">{children}</div>;
};
